/**
 * managerReviewV2Api — proxy calls for Manager Review v2.
 *
 * GET /api/people/manager-review-v2/access
 *   Whether the caller may open v2 (pilot testers until MANAGER_REVIEW_V2_LIVE).
 * GET /api/people/manager-review-v2/employees
 *   Returns the direct reports of the logged-in manager (+ test employees for pilot testers).
 *   Also acts as the access gate: 403 if the caller has no direct reports.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';
import type { Employee } from '../types/managerReviewV2';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-520260363689.europe-west3.run.app';

export interface FetchEmployeesResult {
  employees: Employee[];
}

export class PeopleDatabaseNotConfiguredError extends Error {
  constructor() {
    super('People database not configured on the server.');
    this.name = 'PeopleDatabaseNotConfiguredError';
  }
}

export class NotAManagerError extends Error {
  details: string;
  constructor(details?: string) {
    super('You do not have any direct reports in the People database.');
    this.name = 'NotAManagerError';
    this.details = details || '';
  }
}

/**
 * Fetch the direct reports of the currently authenticated manager.
 * Throws NotAManagerError (403) or PeopleDatabaseNotConfiguredError (503)
 * on expected failure conditions.
 */
export async function fetchDirectReports(): Promise<FetchEmployeesResult> {
  const res = await authenticatedFetch(
    `${PROXY_URL}/api/people/manager-review-v2/employees`,
    { suppressErrorStatuses: [403, 503] },
  );

  if (res.status === 403) {
    const body = await res.json().catch(() => ({}));
    throw new NotAManagerError(body?.details);
  }

  if (res.status === 503) {
    throw new PeopleDatabaseNotConfiguredError();
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Server error ${res.status}`);
  }

  return res.json() as Promise<FetchEmployeesResult>;
}

/** Whether this person may open v2, and whether v2 is live for everyone. The gate lives in the proxy. */
export async function fetchManagerReviewV2Access(): Promise<{ enabled: boolean; live: boolean }> {
  // Throws on failure (authenticatedFetch does for non-OK): after cutover, guessing "v1" would let
  // a manager write a review into the retired drafts table. The caller retries, then shows an error.
  const res = await authenticatedFetch(`${PROXY_URL}/api/people/manager-review-v2/access`);
  const data = await res.json();
  // Live only together with enabled: a half answer must never send someone to a page that 403s.
  const enabled = data?.enabled === true;
  return { enabled, live: enabled && data?.live === true };
}
